import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Autocomplete } from "@material-ui/lab";
import { TextField, CircularProgress } from "@material-ui/core";

// Module-level constant — previously declared inside the component
// where every render produced a fresh array reference, which made
// the dependency array on the first useEffect always change and the
// effect re-run on every render (body was a no-op most of the time,
// but still wasteful). Promoting to module scope makes the list a
// single referentially-stable object.
const TIME_ZONES = [
  { label: "Alaska", time_zone: "America/Juneau" },
  { label: "Pacific Time", time_zone: "America/Los_Angeles" },
  { label: "Mountain Time", time_zone: "America/Denver" },
  { label: "Arizona", time_zone: "America/Phoenix" },
  { label: "Central Time", time_zone: "America/Chicago" },
  { label: "Eastern Time", time_zone: "America/New_York" },
  { label: "Indiana (East)", time_zone: "America/Indiana/Indianapolis" },
  { label: "Newfoundland", time_zone: "America/St_Johns" },
  { label: "Puerto Rico", time_zone: "America/Puerto_Rico" },
];

const TimeZoneSelector = ({
  time_zone,
  setFieldValue,
  helperText,
  error,
  onBlur,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(TIME_ZONES);
  const [search, setSearch] = useState("");
  const loading = open && options.length === 0;

  useEffect(() => {
    if (!loading) {
      return undefined;
    }

    if (open) setOptions(TIME_ZONES);
  }, [open, loading, search]);

  useEffect(() => {
    if (!open) setOptions([]);
  }, [open]);

  return (
    <Autocomplete
      id="school_time_zone_input_dialog"
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={time_zone}
      inputValue={search}
      onChange={(e, newValue) =>
        setFieldValue("time_zone", newValue?.time_zone)
      }
      onInputChange={(e, newInputValue) => setSearch(newInputValue)}
      getOptionSelected={(option, value) => option.time_zone === value}
      getOptionLabel={(option) => {
        // TZ-783 follow-up: Autocomplete invokes this callback with
        // either (a) the full option object {label, time_zone} when
        // it's iterating the dropdown, or (b) the raw `value` prop
        // when the value isn't a member of `options` yet — which is
        // the case on a fresh "New Organization" form where the
        // initial time_zone is "" (added in PR #544 review fix as
        // a defensive controlled-input default). Without these
        // guards the `.find(...).label` chain blows up on undefined
        // and crashes the whole React tree to white-screen, which
        // is exactly the regression Janaya reported on prod.
        if (option && typeof option === "object" && option.label) {
          return option.label;
        }
        const lookup = typeof option === "string" && option ? option : time_zone;
        if (!lookup) return "";
        const match = TIME_ZONES.find((x) => x.time_zone === lookup);
        return match ? match.label : "";
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required
          name="time_zone"
          label="Time Zone"
          variant="outlined"
          error={error}
          helperText={helperText}
          onBlur={onBlur}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

TimeZoneSelector.propTypes = {
  time_zone: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
  helperText: PropTypes.node,
  error: PropTypes.bool,
  onBlur: PropTypes.func,
};

export default TimeZoneSelector;
